import { api } from 'redux/api/index.api';
import { getProjectsServiceVersion } from 'utils/constants';

export const projectMembersApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchProjectMembers: builder.query({
      query: ({
        projectId,
        searchValue,
        filters,
        withProcessingMembers = true,
        withDisabledMembers = false,
        limit = 500,
        offset = 0,
      }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/members`,
        method: 'POST',
        params: {
          limit,
          offset,
        },
        body: {
          search_term: searchValue,
          roles: filters?.roles,
          with_processing_members: withProcessingMembers,
          with_disabled_members: withDisabledMembers,
        },
      }),
      providesTags: ['ProjectMembersList'],
    }),
    fetchProjectNonMembers: builder.query({
      query: ({ projectId, searchValue, limit = 500, offset = 0 }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/non-members`,
        method: 'GET',
        params: {
          limit,
          offset,
          search_term: searchValue,
        },
      }),
      providesTags: ['ProjectNonMembersList'],
    }),
    fetchProjectRoles: builder.query({
      query: ({ projectId, sameAndBelow }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/project-roles`,
        method: 'GET',
        params: {
          same_and_below: sameAndBelow,
        },
      }),
      transformResponse: response =>
        !response?.project_roles
          ? response
          : {
              ...response,
              project_roles: response.project_roles.toSorted((a, b) => a.position - b.position),
            },
      providesTags: ['ProjectRoles'],
    }),
    addUsersToProject: builder.mutation({
      query: ({ projectId, emailsByProjectRoleId }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/add-members`,
        method: 'POST',
        body: {
          project_role_users: emailsByProjectRoleId,
        },
      }),
      invalidatesTags: ['ProjectMembersList', 'ProjectNonMembersList', 'ProjectRoles'],
    }),
    updateProjectRoles: builder.mutation({
      query: ({ projectId, roleId, userIds }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/edit-members-roles`,
        method: 'POST',
        body: {
          project_role_id: roleId,
          user_ids: userIds,
        },
      }),
      invalidatesTags: ['ProjectMembersList', 'ProjectRoles'],
    }),
    removeMembersFromProject: builder.mutation({
      query: ({ projectId, userIds }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/remove-members`,
        method: 'POST',
        body: {
          user_ids: userIds,
        },
      }),
      invalidatesTags: ['ProjectMembersList', 'ProjectNonMembersList', 'ProjectRoles'],
    }),
    fetchReportRecipients: builder.query({
      query: ({ projectId, organizationId }) => ({
        url: `${getProjectsServiceVersion()}/projects/${projectId}/members`,
        method: 'POST',
        body: {
          organization_id: organizationId,
          project_id: projectId,
        },
      }),
      transformResponse: (response, meta, arg) => {
        if (!response?.members) return [];
        const { currentUserId } = arg;
        return response.members
          .map(({ user_id, first_name, last_name, email, picture }) => ({
            user_id,
            first_name,
            last_name,
            email,
            picture,
            added: false,
          }))
          .filter(item => !currentUserId || item.user_id !== currentUserId)
          .sort((a, b) => a.first_name?.localeCompare(b.first_name));
      },
      providesTags: ['ReportRecipients'],
    }),
  }),
  overrideExisting: true,
});

export const {
  useFetchProjectMembersQuery,
  useLazyFetchProjectMembersQuery,
  useFetchProjectNonMembersQuery,
  useLazyFetchProjectNonMembersQuery,
  useFetchProjectRolesQuery,
  useAddUsersToProjectMutation,
  useUpdateProjectRolesMutation,
  useRemoveMembersFromProjectMutation,
  useFetchReportRecipientsQuery,
} = projectMembersApi;
